import { useEffect, useRef, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { ErrorBoundary } from '@/components/common/error-boundary'

interface GridPanelFrameProps {
  panelId: string
  /**
   * Whether this panel type has an internal scroll that would hijack the page
   * wheel, so it needs the click-to-interact shield. Non-scrolling panels
   * (charts, scorecards) skip it and stay always-interactive.
   */
  shielded: boolean
  /**
   * When false (and shielded), an overlay covers the body: the wheel scrolls the
   * page past the panel instead of the panel's own scroller hijacking it, and a
   * "Click to interact" hint shows. When true, the body is fully interactive.
   */
  active: boolean
  /**
   * True while a filter chip in the page header was drilled from this panel
   * (a double-clicked bar, cell, or flow). Paints the tile border in the
   * primary accent so the drill's origin stays visible.
   */
  drillSource?: boolean
  /** The panel's toolbar — the real PanelHeader, same as mosaic mode. */
  header: ReactNode
  /**
   * Fit-to-content mode: the body takes its natural height instead of
   * filling the cell, and reports that height (header and padding included)
   * whenever it changes, so the grid can size the cell to it. Bodies that
   * stretch to fill (`h-full`) lay out at their content height here, so a
   * panel never scrolls inside its tile.
   */
  onContentHeight?: (px: number) => void
  children: ReactNode
}

/** The frame's own chrome around the body: p-1 top and bottom, the h-7 header. */
const FRAME_CHROME_PX = 8 + 28

/**
 * Window chrome for a react-grid-layout cell — styled to match the mosaic
 * MosaicWindow (see grid-workspace.css). The whole header row is the drag handle
 * (`.grid-panel-header`); the header + resize handles stay interactive
 * regardless of `active`. Click-to-interact only gates the body — see
 * GridWorkspace for the activation logic.
 */
export function GridPanelFrame({
  panelId,
  shielded,
  active,
  drillSource = false,
  header,
  onContentHeight,
  children,
}: GridPanelFrameProps) {
  const showShield = shielded && !active
  const fit = onContentHeight != null
  const bodyRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!fit || !bodyRef.current) return
    const el = bodyRef.current
    const report = () => onContentHeight(Math.ceil(el.offsetHeight) + FRAME_CHROME_PX)
    const ro = new ResizeObserver(report)
    ro.observe(el)
    report()
    return () => ro.disconnect()
  }, [fit, onContentHeight])
  return (
    <div
      data-grid-panel={panelId}
      // EVERY panel shows the active state when clicked, not just shielded
      // ones. It is drawn by recoloring the tile's own glass border (see
      // grid-workspace.css) rather than a ring outside the box: the grid sits
      // flush against the page header and the nav rail, so an outside ring
      // lost its top and left edges to the scroll container.
      data-active={active ? 'true' : undefined}
      // Drill origin: same border recolor as active (see grid-workspace.css).
      data-drill-source={drillSource ? 'true' : undefined}
      className={cn(
        // Surface comes from .edge-grid-tile (grid-workspace.css) — the same
        // panel-surface variables the mosaic window paints.
        // `group/panel` is NAMED so panels can reveal their own chrome on
        // whole-tile hover (and on `data-active`) without colliding with any
        // unnamed `group` a panel uses internally. See ScorecardHeader.
        'group/panel edge-grid-tile flex h-full flex-col overflow-hidden rounded-lg p-1',
      )}
    >
      <div className="grid-panel-header flex h-7 shrink-0 cursor-grab items-center active:cursor-grabbing">
        <div className="flex min-w-0 flex-1 items-center overflow-hidden">{header}</div>
      </div>
      {/* Transparent body — the tile paints the panel surface; an opaque
          bg-card here was cutting the gradient off below the header. */}
      <div
        ref={bodyRef}
        className={cn(
          'relative overflow-hidden rounded-sm',
          // Filling the cell is what makes a tall body scroll inside it; in
          // fit mode the body is as tall as its content and the cell follows.
          fit ? 'shrink-0' : 'min-h-0 flex-1',
        )}
      >
        <ErrorBoundary inline>{children}</ErrorBoundary>
        {showShield && (
          // Shield: captures pointer so a click just activates (doesn't reach
          // the content), and — being non-scrollable — lets the wheel bubble to
          // the page. z-20 sits below the resize handles (z-30 in the css).
          <div
            data-panel-overlay={panelId}
            aria-hidden="true"
            className="group absolute inset-0 z-20 cursor-pointer"
          >
            <div className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full border border-border bg-background/85 px-2 py-0.5 text-[11px] font-medium text-muted-foreground opacity-0 shadow-sm backdrop-blur-sm transition-opacity duration-150 group-hover:opacity-100">
              Click to interact
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
